import { TcrLayout } from "@/tcr/TcrLayout";
import { StatusChip, Kicker, LocalBoundary } from "@/tcr/ui";
import {
    NODES,
    GATES,
    AI_MAY,
    AI_MAY_NOT,
    AI_LINE,
    STANDARDS,
    STANDARDS_LINE,
    STANDARDS_NOT_CLAIMED,
    KEY_LINE,
} from "@/tcr/data";

export default function TcrArchitecture() {
    return (
        <TcrLayout>
            <section data-testid="architecture-hero">
                <Kicker>Role architecture</Kicker>
                <h1 className="mt-3 max-w-4xl text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
                    Five bounded, independently operated role nodes
                </h1>
                <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600">
                    Every material participant operates a separate role node. Raw local
                    evidence stays with the organisation that holds it; only bounded
                    proofs, decisions, requests and receipts move between organisations.
                    KATLAS governs the decision and handoff layer around their existing
                    systems — supplier product-lifecycle, service-management, hospital
                    asset-management, ITSM and quality-management platforms remain in
                    place.
                </p>
                <p className="mt-4 border-l-2 border-blue-950 pl-4 text-sm font-medium leading-relaxed text-slate-900">
                    {KEY_LINE}
                </p>
            </section>

            <div className="mt-10 grid gap-5 md:grid-cols-2" data-testid="architecture-nodes">
                {NODES.map((n, i) => (
                    <LocalBoundary key={n.id} testId={`arch-node-${n.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-medium text-slate-900">
                                Node {i + 1} — {n.name}
                            </p>
                            <StatusChip tone="blue">Locally operated role node</StatusChip>
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-slate-500">{n.desc}</p>
                        <div className="mt-4 grid gap-4 sm:grid-cols-3">
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-wide text-slate-400">Holds locally</p>
                                <ul className="mt-1 space-y-0.5 text-sm text-slate-600">
                                    {n.holds.map((h) => <li key={h}>· {h}</li>)}
                                </ul>
                            </div>
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-wide text-green-700">May</p>
                                <ul className="mt-1 space-y-0.5 text-sm text-slate-600">
                                    {n.may.map((m) => <li key={m}>· {m}</li>)}
                                </ul>
                            </div>
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-wide text-red-700">May not</p>
                                <ul className="mt-1 space-y-0.5 text-sm text-slate-600">
                                    {n.mayNot.map((m) => <li key={m}>· {m}</li>)}
                                </ul>
                            </div>
                        </div>
                    </LocalBoundary>
                ))}
            </div>

            <section className="mt-14 rounded-sm border border-slate-200 bg-white p-8" data-testid="architecture-gates">
                <Kicker>Control gates</Kicker>
                <h2 className="mt-3 text-2xl font-medium tracking-tight text-slate-900">
                    An action is permitted only when its gates are satisfied
                </h2>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
                    The actor must hold the appropriate role, required evidence must be
                    present, the relevant policy version must apply, the journey must be
                    in the correct state, and unresolved exceptions must not prohibit
                    progression.
                </p>
                <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {GATES.map((g, i) => (
                        <div key={g} className="flex items-center gap-3 rounded-sm border border-slate-200 bg-slate-50 px-3 py-2">
                            <span className="font-mono text-[10px] text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                            <span className="text-sm text-slate-700">{g}</span>
                        </div>
                    ))}
                </div>
            </section>

            <section className="mt-8 rounded-sm border border-slate-200 bg-white p-8" data-testid="architecture-ai">
                <Kicker>AI assistant authority</Kicker>
                <h2 className="mt-3 text-2xl font-medium tracking-tight text-slate-900">{AI_LINE}</h2>
                <div className="mt-5 grid gap-6 sm:grid-cols-2">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-wide text-green-700">The AI assistant may</p>
                        <ul className="mt-1 space-y-0.5 text-sm text-slate-600">
                            {AI_MAY.map((m) => <li key={m}>· {m}</li>)}
                        </ul>
                    </div>
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-wide text-red-700">The AI assistant may not</p>
                        <ul className="mt-1 space-y-0.5 text-sm text-slate-600">
                            {AI_MAY_NOT.map((m) => <li key={m}>· {m}</li>)}
                        </ul>
                    </div>
                </div>
                <p className="mt-4 text-xs leading-relaxed text-slate-500">
                    No AI assistant may exercise a role authority merely because it can
                    recommend an action.
                </p>
            </section>

            <section className="mt-8 rounded-sm border border-slate-200 bg-white p-8" data-testid="architecture-standards">
                <Kicker>Illustrative standards landscape</Kicker>
                <h2 className="mt-3 text-2xl font-medium tracking-tight text-slate-900">{STANDARDS_LINE}</h2>
                <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {STANDARDS.map(([code, desc]) => (
                        <div key={code} className="rounded-sm border border-slate-200 bg-slate-50 px-4 py-3">
                            <p className="font-mono text-sm font-medium text-blue-950">{code}</p>
                            <p className="mt-0.5 text-xs leading-relaxed text-slate-600">{desc}</p>
                        </div>
                    ))}
                </div>
                <div className="mt-5 border-t border-slate-100 pt-4">
                    <p className="font-mono text-[10px] uppercase tracking-wide text-red-700">This application does not claim</p>
                    <ul className="mt-1 grid gap-x-8 gap-y-0.5 text-sm text-slate-600 sm:grid-cols-2">
                        {STANDARDS_NOT_CLAIMED.map((s) => <li key={s}>· {s}</li>)}
                    </ul>
                </div>
            </section>
        </TcrLayout>
    );
}
